import React from "react";
import { ShieldCheck, Award, Leaf, HeartPulse, FileCheck2, ClipboardList } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import AboutRelated from "@/components/AboutRelated";

// Compliance-safe copy:
// - ISO 9001/14001/45001 already stated publicly across the site (About + Accreditations).
// - No invented certificate numbers, auditor names, cert scopes or expiry dates.
// - Certificate copies available on request (standard industry practice).

const STANDARDS = [
  {
    icon: Award,
    code: "ISO 9001",
    title: "Quality Management",
    body: "Externally audited processes covering engineering delivery, documentation and continual improvement.",
  },
  {
    icon: Leaf,
    code: "ISO 14001",
    title: "Environmental Management",
    body: "Waste, spill, emissions and environmental risk controls applied on every job.",
  },
  {
    icon: HeartPulse,
    code: "ISO 45001",
    title: "Occupational H&S",
    body: "A structured health & safety management system for our team and for site visitors.",
  },
];

const PROCESS_ITEMS = [
  {
    icon: ClipboardList,
    title: "Written RAMS",
    body: "Site-specific Risk Assessments and Method Statements are produced before work starts and reviewed on arrival.",
  },
  {
    icon: FileCheck2,
    title: "Permits & Isolations",
    body: "We work to your permit-to-work regime — LOTO / safe electrical isolations documented with photo evidence where appropriate.",
  },
  {
    icon: ShieldCheck,
    title: "Sign-off Documentation",
    body: "Test certificates, EIC/EICR, commissioning records and O&M files are issued via the Lutrom Operations Portal for traceability.",
  },
];

const QualityAndCompliancePage = () => (
  <div data-testid="about-quality-page">
    <Seo
      title="Quality & Compliance"
      description="Lutrom Engineering operates externally audited ISO 9001, 14001 and 45001 management systems. RAMS, permits, isolations and sign-off documentation on every job. Certificate copies available on request."
      path="/about/quality-and-compliance"
    />

    <section className="relative overflow-hidden">
      <PhotoBackdrop src="/gallery/db-panel-2.jpg" position="center 40%" />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20 relative">
        <p className="eyebrow">About · Quality &amp; Compliance</p>
        <h1
          className="font-display mt-6 text-white"
          style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 0.98 }}
          data-testid="quality-heading"
        >
          QUALITY,
          <br />
          <span className="gradient-blue-text">EXTERNALLY AUDITED.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-[16px] leading-relaxed text-[color:var(--lt-text-dim)]">
          Every job we deliver sits on top of three externally audited management
          systems — ISO 9001 quality, ISO 14001 environment and ISO 45001
          occupational health &amp; safety. Certificate copies are available on
          request.
        </p>
      </div>
    </section>

    <Breadcrumb items={[{ label: "About", to: "/about" }, { label: "Quality & Compliance" }]} />

    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16">
      <p className="eyebrow">Management systems</p>
      <h2
        className="font-display mt-4 text-white"
        style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1 }}
      >
        THREE STANDARDS. ONE INTEGRATED SYSTEM.
      </h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {STANDARDS.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.code}
              className="service-card rounded-xl p-6"
              data-testid={`quality-standard-${s.code.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              <span className="icon-tile" style={{ width: 44, height: 44 }}>
                <Icon size={20} />
              </span>
              <p
                className="font-display mt-5 text-white"
                style={{ fontSize: 20, letterSpacing: "0.03em" }}
              >
                {s.code}
              </p>
              <p className="text-[11px] uppercase tracking-widest text-[color:var(--lt-blue-soft)] font-semibold mt-1">
                {s.title}
              </p>
              <p className="mt-3 text-[13.5px] text-[color:var(--lt-text-dim)] leading-relaxed">
                {s.body}
              </p>
            </div>
          );
        })}
      </div>
    </section>

    <section className="relative overflow-hidden mt-24 py-20 lg:py-24">
      <PhotoBackdrop src="/gallery/commissioning-cage.jpg" position="center 40%" dim={1.08} />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
        <p className="eyebrow">How we work on site</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(28px, 3.6vw, 44px)", lineHeight: 1 }}
        >
          COMPLIANCE, BAKED INTO EVERY VISIT.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {PROCESS_ITEMS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="glass-card rounded-xl p-6"
                data-testid={`quality-process-${p.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
              >
                <span className="icon-tile" style={{ width: 44, height: 44 }}>
                  <Icon size={20} />
                </span>
                <h3
                  className="font-display mt-5 text-white"
                  style={{ fontSize: 19, letterSpacing: "0.02em" }}
                >
                  {p.title.toUpperCase()}
                </h3>
                <p className="mt-3 text-[13.5px] text-[color:var(--lt-text-dim)] leading-relaxed">
                  {p.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <p className="eyebrow">Documentation on request</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(26px, 3.2vw, 40px)", lineHeight: 1 }}
        >
          NEED PROOF FOR YOUR
          <br />
          <span className="gradient-blue-text">CONTRACTOR PACK?</span>
        </h2>
      </div>
      <div className="lg:col-span-7 space-y-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
        <p>
          We are happy to supply the following to prospective and existing clients
          under NDA where appropriate:
        </p>
        <ul className="space-y-2 list-disc pl-5 marker:text-[color:var(--lt-blue-bright)]">
          <li>Current ISO 9001, ISO 14001 and ISO 45001 certificate copies</li>
          <li>Public and Employers&apos; Liability insurance schedules</li>
          <li>Sample RAMS templates and permit-to-work workflow</li>
          <li>Engineer competency evidence &amp; qualifications register</li>
          <li>Environmental &amp; sustainability policy</li>
          <li>Modern Slavery and Anti-Bribery statements</li>
        </ul>
        <p>
          Email <a href="mailto:enquiries@lutrom.co.uk" className="text-[color:var(--lt-blue-bright)] hover:underline">enquiries@lutrom.co.uk</a> or use the enquiry form.
        </p>
      </div>
    </section>

    <CTABanner
      variant="standard"
      title="Add Lutrom to your approved supplier list."
      body="Contractor pre-qualification questionnaires (PQQ) and vendor forms turned around within a working day."
      phoneLocation="about_quality_cta"
    />

    <AboutRelated excludeSlug="quality-and-compliance" />
  </div>
);

export default QualityAndCompliancePage;
